import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellOff, MapPin, ArrowRight, Thermometer, Share2, CheckCheck, PackageSearch, Truck, Weight } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { tempTone, waShare, loadShareMessage, truckShareMessage, fmtDate } from "@/lib/constants";

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchItems = useCallback(async () => {
    try { const r = await api.get("/notifications"); setItems(r.data); } catch (e) { console.error("Failed to load notifications:", e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); setItems((p) => p.map((n) => n.id === id ? { ...n, read: true } : n)); } catch (e) { console.error("Failed to mark read:", e); }
  };
  const markAll = async () => {
    try { await api.post("/notifications/read-all"); setItems((p) => p.map((n) => ({ ...n, read: true }))); } catch (e) { console.error("Failed to mark all read:", e); }
  };

  const isTruck = (n) => (n.type === "truck_match" || n.type === "truck_expiry") && n.truck;

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">Match Alerts</h1>
          <p className="text-muted-foreground text-sm mt-1">Loads and trucks that match what you've posted.</p>
        </div>
        {items.some((n) => !n.read) && (
          <Button data-testid="mark-all-read-btn" variant="secondary" size="sm" onClick={markAll} className="rounded-full"><CheckCheck className="w-4 h-4 mr-1.5" />Mark all read</Button>
        )}
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm py-16 text-center">Loading alerts…</p>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl">
          <BellOff className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
          <p className="text-muted-foreground">No match alerts yet. Post a load or truck and we'll ping you when a match appears.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((n) => {
            const truck = isTruck(n);
            const shareHref = truck ? waShare(truckShareMessage(n.truck)) : waShare(loadShareMessage(n.load || {}));
            return (
              <div key={n.id} data-testid="notification-item"
                   className={`rounded-xl border p-5 transition-all ${n.read ? "border-border bg-card/40" : "border-primary/40 bg-primary/5"}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${n.read ? "bg-secondary" : "bg-primary/15 border border-primary/30"}`}>
                    {truck ? <Truck className={`w-4 h-4 ${n.read ? "text-muted-foreground" : "text-primary"}`} /> : <Bell className={`w-4 h-4 ${n.read ? "text-muted-foreground" : "text-primary"}`} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-sm">{n.title}</p>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-primary shrink-0" />}
                    </div>

                    {truck ? (
                      <div className="mt-2 rounded-lg border border-border bg-background/40 p-3">
                        <div className="flex items-center gap-2 font-heading font-semibold text-sm mb-2">
                          <Truck className="w-3.5 h-3.5 text-primary" /><span className="truncate">{n.truck.truck_type}</span>
                          <span className="text-muted-foreground font-normal text-xs">at {n.truck.current_location}{n.truck.destination ? ` → ${n.truck.destination}` : ""}</span>
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Weight className="w-3 h-3" />{n.truck.capacity}T</span>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${tempTone(n.truck.temp_min)}`}><Thermometer className="w-3 h-3" />{n.truck.temp_min}°/{n.truck.temp_max}°</span>
                          <span>Available {fmtDate(n.truck.available_date)}</span>
                          {n.for_route && <span>· for {n.for_route}</span>}
                        </div>
                      </div>
                    ) : n.load ? (
                      <div className="mt-2 rounded-lg border border-border bg-background/40 p-3">
                        <div className="flex items-center gap-2 font-heading font-semibold text-sm mb-2">
                          <MapPin className="w-3.5 h-3.5 text-primary" /><span className="truncate">{n.load.origin}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" /><span className="truncate">{n.load.destination}</span>
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><PackageSearch className="w-3 h-3" />{n.load.material_type} · {n.load.weight}T</span>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${tempTone(n.load.temp_min)}`}><Thermometer className="w-3 h-3" />{n.load.temp_min}°/{n.load.temp_max}°</span>
                          <span>Pickup {fmtDate(n.load.pickup_date)}</span>
                        </div>
                      </div>
                    ) : null}

                    <div className="flex items-center gap-2 mt-3">
                      <a data-testid="notif-share-btn" href={shareHref} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-border hover:border-[#25D366]/60 hover:text-[#25D366] transition-all">
                        <Share2 className="w-3.5 h-3.5" /> Share on WhatsApp
                      </a>
                      <Button data-testid="notif-view-btn" size="sm" variant="ghost" className="rounded-full h-8 text-xs" onClick={() => navigate(n.type === "truck_expiry" ? "/app/postings" : (truck ? "/app/trucks" : "/app"))}>{n.type === "truck_expiry" ? "Repost" : "View board"}</Button>
                      {!n.read && <Button data-testid="notif-read-btn" size="sm" variant="ghost" className="rounded-full h-8 text-xs text-muted-foreground" onClick={() => markRead(n.id)}>Mark read</Button>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
